// eslint-disable-next-line import/no-extraneous-dependencies
import { useLocation } from '@gatsbyjs/reach-router';
import { useMemo } from 'react';
import { CsBlock } from './blockMappers/mappingUtils';

type PaginationState = {
  page?: number;
};

const PAGINATION_LISTS = [
  'related_content_card_list_with_pagination',
  'document_download_link_list_with_pagination',
];

/**
 * Gets the page number from the location state for the List with Pagination components.
 * This will work if only one block that uses the List with Pagination component is present on the page.
 *
 * @param blocks - the blocks inside the 'Flexible page content' section
 *
 * @returns the page number to be used in the List with Pagination component.
 */
const usePaginationNavigationState = (blocks: CsBlock[]): number | undefined => {
  const pageState = useLocation().state as PaginationState;
  const hasOnlyOnePaginationList = useMemo(() => {
    const paginationLists = blocks.filter((block) => {
      return Object.entries(block).find(([key, value]) => {
        return !!value && PAGINATION_LISTS.includes(key);
      })?.[0];
    });
    return paginationLists.length === 1;
  }, [blocks]);

  const currentPage = useMemo(() => {
    if (!hasOnlyOnePaginationList) {
      return undefined;
    }

    if (!pageState) {
      return undefined;
    }
    const pageNumber = pageState.page;
    if (!pageNumber) {
      return undefined;
    }

    const numberPageNumber = Number(pageNumber);
    if (isNaN(numberPageNumber)) {
      return undefined;
    }

    return numberPageNumber;
  }, [hasOnlyOnePaginationList, pageState]);

  return currentPage;
};

export default usePaginationNavigationState;
